import {
  CivilStatus,
  Currency,
  DataTypeOption,
  DataTypeOptions,
  Day,
  DefaultFrequencies,
  DefaultMonths,
  IdentityDocumentOptions,
  Employment,
  EmploymentRelationship,
  Frequency,
  FuturePlan,
  Gender,
  InvestorProfile,
  Loan,
  Month,
  MonthlyExpenditure,
  SavingOrInvestment,
  University
} from '@core/constants/data-types.constants';
import { Configurations } from './coinscrap/configurations.model';
import { CurrencyAmount } from './currency-amount.model';
import { CurrencyExchange } from './currency-exchange';
import { Product } from './product.model';
import { SameAs } from './utility-types';
import { IdentityDocument } from './identity-document.model';

export interface Config {
  contactData: ContactData;
  ageMilestones: AgeMilestones;
  phoneConstraints: PhoneConstraints;
  currencyConstraints: CurrencyConstraints;
  currencyPosition: 'left' | 'right';
  defaultCurrency: Currency;
  currencies: DataTypeOptions<Currency>;
  currencyExchange: CurrencyExchange;
  days: DataTypeOptions<Day>;
  months: DataTypeOptions<Month>;
  genders: DataTypeOptions<Gender>;
  identityDocumentOptions: DataTypeOptions<IdentityDocumentOptions>;
  identityDocumentConstraints: IdentityDocumentConstraints[];
  civilStatuses: DataTypeOptions<CivilStatus>;
  universities: DataTypeOptions<University>;
  universitiesByLocation: UniversitiesByLocation;
  employmentRelationships: DataTypeOptions<EmploymentRelationship>;
  employments: DataTypeOptions<Employment>;
  monthlyExpenditures: DataTypeOptions<MonthlyExpenditure>;
  savingsAndInvestments: DataTypeOptions<SavingOrInvestment>;
  investorProfiles: DataTypeOptions<InvestorProfile>;
  futurePlans: DataTypeOptions<FuturePlan>;
  loans: DataTypeOptions<Loan>;
  products: {
    [key: string]: Product;
  };
  sourceConfigurations?: Configurations;
  frequencies: DataTypeOptions<string>;
}

export const DefaultCurrencyMask = 'separator.0';
export const DefaultCurrencyDecimalsMask = 'separator.2';
export const DefaultCurrencyThousandSeparator = '.';
export const DefaultCurrencyDecimalMarker = ',';

export interface ContactData {
  phone?: string;
  email?: string;
}

export interface AgeMilestones {
  retirement: number;
  university: number;
  /** Its value should be equal to or less than the 'university' one */
  childMaxAge: SameAs<AgeMilestones, 'university'>;
  childMinAge: number;
  dependantMaxAge: number;
  dependantMinAge: number;
}

export interface PhoneConstraints {
  mask?: string;
  phoneValidator: RegExp;
}

export interface CurrencyConstraints {
  masking?: MaskConstraints;
}

export interface IdentityDocumentConstraints {
  key: string;
  mask?: string;
  identityDocumentValidator: RegExp;
}

export interface MaskConstraints {
  mask?: string;
  thousandSeparator?: string;
  decimalMarker?: '.' | ',';
}

export interface UniversitiesByLocation {
  [key: string]: {
    option: DataTypeOption<University>;
    params: {
      universityCost: CurrencyAmount;
      travelCosts?: CurrencyAmount;
    };
  }[];
}

export const DefaultConfig: Config = {
  contactData: {
    phone: '5572560194',
    email: 'AtencionClientes@mapfre.com.mx'
  },
  ageMilestones: {
    retirement: 65,
    university: 18,
    childMaxAge: 18,
    childMinAge: 0,
    dependantMinAge: 1,
    dependantMaxAge: 120
  },
  phoneConstraints: {
    mask: '000000000',
    phoneValidator: /^(\d{9})$/
  },
  identityDocumentConstraints: [],
  currencyConstraints: {
    masking: {
      mask: DefaultCurrencyMask,
      thousandSeparator: DefaultCurrencyThousandSeparator,
      decimalMarker: DefaultCurrencyDecimalMarker
    }
  },
  currencyPosition: 'right',
  defaultCurrency: '',
  currencies: [],
  currencyExchange: {},
  days: [],
  months: DefaultMonths,
  genders: [],
  identityDocumentOptions: [],
  civilStatuses: [],
  universities: [],
  universitiesByLocation: {},
  employmentRelationships: [],
  employments: [],
  monthlyExpenditures: [],
  savingsAndInvestments: [],
  investorProfiles: [],
  futurePlans: [],
  loans: [],
  products: {},
  frequencies: DefaultFrequencies
};
